import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import tw from 'twin.macro';

import { TagRowType } from '@insights_module/types/Megaphone';
import { Checkbox } from '@components/Checkbox';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { useNotification } from '@insights_module/hooks/useNotification';
import { ThreeDots } from '@icons/ThreeDots';
import { FiltersIcon } from '@icons/FiltersIcon';
import { ClipboardIcon } from '@icons/ClipboardIcon';
import { CloseIcon } from '@icons/CloseIcon';
import { EditIcon } from '@icons/EditIcon';
import { DuplicateIcon } from '@icons/DuplicateIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { objectToToken } from '@utils/commonUtils';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { IntegrationType } from '@insights_module/types/Integrations';
import { EpisodesIconDark, EpisodesIconLight } from '@icons/EpisodesIcon';
import { useTheme } from '@themes/useTheme';

const Container = tw.div`flex flex-col items-start justify-start w-full`;
const Header = tw.div`w-full h-full border border-solid border-gray-1 border-b-0 rounded-t-[8px] bg-page-background p-[12px] flex flex-row items-center justify-between gap-[8px]`;
const Body = tw.div`w-full h-full border border-solid border-gray-1 border-b-0 border-t-0 bg-page-background p-[12px] bg-page-background flex flex-col items-start justify-start gap-[12px]`;
const Footer = tw.div`w-full border border-solid border-gray-1 border-t-0 rounded-b-[8px] bg-accent-2 px-[12px] py-[6px] flex items-center justify-center`;
const Code = tw.p`text-text-and-icon-invert text-[12px] uppercase line-clamp-1`;
const Title = tw.h3`m-0 text-[14px] max-h-[25px] text-text-and-icon font-semibold truncate`;
const Description = tw.p`text-[11px] h-[30px] text-text-and-icon-muted text-left font-medium line-clamp-2`;
const BoldText = tw.p`text-[11px] text-text-and-icon-muted text-left font-bold leading-[140%] [&>span]:font-medium [&>span]:text-text-and-icon-muted-2`;

const ActionsList = tw.div`absolute bg-neutral-background rounded-[4px] border border-solid border-gray-3 shadow-dropdown p-[4px]`;
const ActionsListItem = tw.button`text-text-and-icon w-full flex items-center justify-start gap-1 hover:bg-gray-2 px-[6px] py-[4px] rounded-[2px] whitespace-nowrap`;
const ActionToggler = tw.button`rounded-[4px] ml-[6px] w-[24px] h-[24px] bg-gray-3 flex items-center justify-center`;
const Divider = tw.div`w-full h-[1px] bg-gray-1 my-1`;

export const CollectionCard = ({
  item,
  integrationType,
  ...rest
}: {
  item: TagRowType;
  integrationType?: IntegrationType;
}) => {
  const { t } = useTranslation();
  const { selectedRows, setSelectedRows } = useIntegrationsContext();
  const { details, eligible_episodes, active_episode_publish_date_range, code } = item;
  const { notify, NTFC_TYPE } = useNotification();

  const handleChange = () => {
    setSelectedRows(
      selectedRows?.includes(details.tag_id as never)
        ? selectedRows.filter((id) => id !== details.tag_id)
        : [...selectedRows, details.tag_id]
    );
  };

  const handleCopyToClipboard = async (val: string) => {
    if (val) {
      try {
        await navigator.clipboard.writeText(val);

        notify({
          type: NTFC_TYPE.INFO,
          message: t('common.copied_to_clipboard'),
          duration: 1000,
        });
      } catch (err) {
        console.error('Failed to copy value to clipboard: ', err);
      }
    }
  };

  return (
    <Container {...rest}>
      <Header>
        <Checkbox
          checked={selectedRows.includes(details.tag_id)}
          onChange={handleChange}
          checkboxStyles={tw`border-text-and-icon-muted`}
        />
        <Title title={details.title}>{details.title}</Title>
        <ActionMenu
          item={item}
          integrationType={integrationType}
          onCopyToClipboard={(val) => handleCopyToClipboard(val)}
        />
      </Header>
      <Body>
        <Description title={details.description || 'No description'}>
          {details.description || 'No description'}
        </Description>
        <BoldText>
          <Trans
            i18nKey={'insights.integrations.collection_card.eligible_episodes'}
            values={{ number: eligible_episodes.toLocaleString() }}>
            Eligible episodes: <span>{eligible_episodes.toLocaleString()}</span>
          </Trans>
        </BoldText>
        <BoldText>
          <Trans
            i18nKey={'insights.integrations.collection_card.active_episodes_publish_date_range'}
            values={{ date_range: active_episode_publish_date_range }}>
            Active episode publish date range: <span>{active_episode_publish_date_range}</span>
          </Trans>
        </BoldText>
      </Body>
      <Footer>
        <Code title={code}>{code}</Code>
      </Footer>
    </Container>
  );
};

const ActionMenu = ({
  item,
  integrationType,
  onCopyToClipboard,
}: {
  item: TagRowType;
  integrationType?: IntegrationType;
  onCopyToClipboard: (val: string) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actionsListRef = useRef(null);
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isDark } = useTheme();

  useOnClickOutside(actionsListRef, () => setIsOpen(false));

  const { setSelectedTableType } = useInsightsContext();

  const {
    selectedAction,
    setSelectedAction,
    setIsEditTagModalOpen,
    setTagToEdit,
    setTagToDelete,
    setIsDeleteTagModalOpen,
    setIsDescriptionModalOpen,
    setTagToDuplicate,
    setIsCreateTagModalOpen,
  } = useIntegrationsContext();

  const onActionClick = () => {
    if (!setSelectedAction) return;

    setSelectedAction(selectedAction === item.details.id ? null : item.details.id);
    setIsOpen(!isOpen);
  };

  const handleDuplicateTag = () => {
    setTagToDuplicate(item.details.id);
    setIsCreateTagModalOpen(true);
    setSelectedAction(null);
  };

  const onClickEditTag = () => {
    setTagToEdit(item.details.id);
    setIsEditTagModalOpen(true);
  };

  const onClickEditDescription = () => {
    setTagToEdit(item.details.id);
    setIsDescriptionModalOpen && setIsDescriptionModalOpen(true);
  };

  const onClickDeleteTag = () => {
    setTagToDelete(item.details.id);
    setIsDeleteTagModalOpen(true);
  };

  const handleApplyTagToInsights = () => {
    const queryToken = objectToToken(item.aql_query);
    navigate(`/insights/episodes?query_token=${queryToken}`);
    queryClient.invalidateQueries([INSIGHTS_QUERY_KEY.infinite_episodes]);
    setSelectedTableType('episodes');
  };

  return (
    <div tw='relative'>
      <ActionToggler onClick={onActionClick}>
        <ThreeDots tw='fill-text-and-icon stroke-text-and-icon' />
      </ActionToggler>
      {isOpen && (
        <ActionsList ref={actionsListRef}>
          <ActionsListItem onClick={handleApplyTagToInsights}>
            {isDark ? <EpisodesIconLight tw='mr-1' /> : <EpisodesIconDark />}

            {t('insights.integrations.ihm_starship.open_in_insights_cta')}
          </ActionsListItem>

          {integrationType == 'megaphone' && (
            <>
              <ActionsListItem onClick={onClickEditTag}>
                <FiltersIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
                {t('insights.integrations.megaphone.tags_cta.view_filter')}
              </ActionsListItem>
              <ActionsListItem onClick={handleDuplicateTag}>
                <DuplicateIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
                {t('insights.integrations.megaphone.tags_cta.duplicate_tag')}
              </ActionsListItem>
            </>
          )}
          <ActionsListItem onClick={() => onCopyToClipboard(item.details.title)}>
            <ClipboardIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
            {t('insights.integrations.megaphone.tags_cta.copy_name')}
          </ActionsListItem>
          <ActionsListItem onClick={() => onCopyToClipboard(item.details.tag_id)}>
            <ClipboardIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
            {t('insights.integrations.megaphone.tags_cta.copy_code')}
          </ActionsListItem>
          {integrationType === 'megaphone' && (
            <ActionsListItem onClick={onClickEditDescription}>
              <EditIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
              {t('insights.integrations.megaphone.tags_cta.edit_description')}
            </ActionsListItem>
          )}
          {integrationType === 'ihm' && (
            <ActionsListItem onClick={onClickEditTag}>
              <EditIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
              {t('insights.integrations.megaphone.tags_cta.edit_tag')}
            </ActionsListItem>
          )}
          <Divider />
          <ActionsListItem
            onClick={onClickDeleteTag}
            tw='text-error-text-icon! hover:bg-error-bg! fill-error-text-icon'>
            <CloseIcon tw='mr-1 w-4 stroke-none fill-inherit' />
            {t('insights.integrations.megaphone.tags_cta.delete_tag')}
          </ActionsListItem>
        </ActionsList>
      )}
    </div>
  );
};
